import { BrowserRouting } from '@aipb/browser/shared/routing';
import { CatalogRouting } from '@aipb/catalog/shared/routing';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { FavoriteCollectionService } from '@aipb/personal-content/data-access';
import { NibbleAction, NibbleType } from '@aipb/personal-content/shared/model';
import { PersonalContentRouting } from '@aipb/personal-content/shared/routing';
import { copyToClipboard } from '@aipb/util/other';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SnackbarService } from '@aipb/ui/snackbar';
import { Injectable } from '@angular/core';
import {
  CatalogNibbleActionHandler,
  CollectionNibbleActionHandler,
  Doc3ifNibbleActionHandler,
  Document3ifNibbleActionHandler,
  Img3ifNibbleActionHandler,
  ImgNibbleActionHandler,
  LinkNibbleActionHandler,
  NibbleActionHandler,
  ServiceHandlerMap,
  TxtNibbleActionHandler,
  TypeHandlerMap
} from './handler';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ResolverService } from '@aipb/resolver';

@Injectable({
  providedIn: 'root'
})
export class NibbleActionService {
  private serviceHandlerMap: ServiceHandlerMap = new ServiceHandlerMap();
  private typeHandlerMap: TypeHandlerMap = new TypeHandlerMap();

  constructor(
    private snackbar: SnackbarService,
    private favoriteCollectionService: FavoriteCollectionService,
    private catalogRouting: CatalogRouting,
    private browserRouting: BrowserRouting,
    private personalContentRouting: PersonalContentRouting,
    private resolverService: ResolverService
  ) {
    this.init();
  }

  init(): void {
    this.initService();
    this.initType();
  }

  initService(): void {
    this.serviceHandlerMap.add('document3if', new Document3ifNibbleActionHandler(this.browserRouting));
  }

  initType(): void {
    this.typeHandlerMap.add(
      'COLL',
      new CollectionNibbleActionHandler(this.favoriteCollectionService, this.personalContentRouting)
    );
    this.typeHandlerMap.add('LINK', new LinkNibbleActionHandler());
    this.typeHandlerMap.add('IMG3F', new Img3ifNibbleActionHandler(this.browserRouting));
    this.typeHandlerMap.add('CROP', new Img3ifNibbleActionHandler(this.browserRouting));
    this.typeHandlerMap.add('IMG', new ImgNibbleActionHandler());
    this.typeHandlerMap.add('DOC3IF', new Doc3ifNibbleActionHandler(this.browserRouting));
    this.typeHandlerMap.add('TXT', new TxtNibbleActionHandler());

    const catalogNibbleActionHandler = new CatalogNibbleActionHandler(this.catalogRouting);
    this.typeHandlerMap.add('REC', catalogNibbleActionHandler);
    this.typeHandlerMap.add('RECDBOX', catalogNibbleActionHandler);
  }

  process(nibbleAction: NibbleAction) {
    const serviceHandler: NibbleActionHandler = this.getServiceHandler(nibbleAction.nibbleCut.cut.service);
    const typeHandler: NibbleActionHandler = this.getTypeHandler(nibbleAction.nibbleCut.nibble.type);

    if (!serviceHandler && !typeHandler) {
      this.snackbar.open(
        `Unknown NibbleAction service=${nibbleAction.nibbleCut.cut.service} type=${nibbleAction.nibbleCut.nibble.type}`,
        'error'
      );
      return;
    }

    if (serviceHandler) {
      serviceHandler.handle(nibbleAction);
      return;
    }

    if (typeHandler) {
      typeHandler.handle(nibbleAction);
      return;
    }
  }

  copyUrl(nibbleAction: NibbleAction) {
    const serviceHandler: NibbleActionHandler = this.getServiceHandler(nibbleAction.nibbleCut.cut.service);
    const typeHandler: NibbleActionHandler = this.getTypeHandler(nibbleAction.nibbleCut.nibble.type);

    if (!serviceHandler && !typeHandler) {
      this.snackbar.open(
        `Unknown NibbleAction service=${nibbleAction.nibbleCut.cut.service} type=${nibbleAction.nibbleCut.nibble.type}`,
        'error'
      );
      return;
    }

    let url = null;

    if (serviceHandler) {
      url = serviceHandler.getUrl(nibbleAction);
      if (!url) {
        this.snackbar.open(`Není dostupné žádné URL`, 'warn');
        return;
      }

      if (serviceHandler.useResolver()) {
        this.resolverService.copyUrl(url).subscribe(() => {
          this.snackbar.open(`URL úspěšně zkopírováno`, 'success');
        });
        return;
      }

      copyToClipboard(url);
      this.snackbar.open(`URL úspěšně zkopírováno`, 'success');
      return;
    }

    if (typeHandler) {
      url = typeHandler.getUrl(nibbleAction);
      if (!url) {
        this.snackbar.open(`Není dostupné žádné URL`, 'warn');
        return;
      }

      if (typeHandler.useResolver()) {
        this.resolverService.copyUrl(url).subscribe(() => {
          this.snackbar.open(`URL úspěšně zkopírováno`, 'success');
        });
        return;
      }

      copyToClipboard(url);
      this.snackbar.open(`URL úspěšně zkopírováno`, 'success');
      return;
    }
  }

  getServiceHandler(service: string): NibbleActionHandler {
    return this.serviceHandlerMap.get(service);
  }

  getTypeHandler(type: NibbleType): NibbleActionHandler {
    return this.typeHandlerMap.get(type);
  }
}
